/* eslint-disable no-unused-vars */

import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { GetEventsSubscriptionsListRequest } from 'src/redux/slices/event-subscriptions';
import {
  CreateEventSubscriptionRequest,
  EventsSubscriptionDetails,
  UpdateEventSubscriptionRequest
} from '../../redux/slices/event-subscriptions/types';
import { useSnackBar } from '../use-snackbar';

const { actions, selectors } = slices.eventSubcriptions;

export const useEventSubscriptions = () => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();

  // selectors
  const events = useAppSelector(selectors.selectEvents);
  const meta = useAppSelector(selectors.selectMeta);
  const isLoading = useAppSelector(selectors.selectIsLoading);
  const filters = useAppSelector(selectors.selectFilters);
  const eventDetails = useAppSelector(selectors.selectDetails);

  // functions
  const getEventSubscriptionsList = useCallback(async () => {
    const response = unwrapResult(
      await dispatch(actions.getEventSubscriptionsListThunk(filters))
    );
    if (!response?.success) {
      const { message, errors } = response;
      snackbar.show({
        severity: 'error',
        message: errors
          ? Object.values(errors).join(', ')
          : message ||
            'Failed to create event. Please try again later or contact support'
      });
    }
  }, [dispatch, filters, snackbar]);

  const createEvent = useCallback(
    async (payload: CreateEventSubscriptionRequest) => {
      const response = unwrapResult(
        await dispatch(actions.createEventSubscriptionsThunk(payload))
      );
      if (response?.success) {
        snackbar.show({
          severity: 'success',
          message: 'Event created successfully'
        });
        return true;
      } else {
        const { message, errors } = response;
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ||
              'Failed to create event. Please try again later or contact support'
        });

        return false;
      }
    },
    [dispatch, snackbar]
  );

  const getEventDetails = useCallback(
    async (id: number) => {
      const response = unwrapResult(
        await dispatch(actions.getEventSubscriptionDetailsThunk(id))
      );
      if (!response?.success) {
        const { message, errors } = response;
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ||
              'Failed to get event details. Please try again later or contact support'
        });
      }
    },
    [dispatch, snackbar]
  );

  const updateEvent = useCallback(
    async (payload: UpdateEventSubscriptionRequest) => {
      const response = unwrapResult(
        await dispatch(actions.updateEventSubscriptionDetailsThunk(payload))
      );
      if (response?.success) {
        snackbar.show({
          severity: 'success',
          message: 'Event updated successfully'
        });
        return true;
      } else {
        const { message, errors } = response;
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ||
              'Failed to update event. Please try again later or contact support'
        });
      }

      return false;
    },
    [dispatch, snackbar]
  );

  const setFilters = useCallback(
    (payload: GetEventsSubscriptionsListRequest) => {
      dispatch(actions.setFilters(payload));
    },
    [dispatch]
  );

  const setVoucherSpecs = useCallback(
    (payload: string) => {
      dispatch(actions.setVoucherSpecs(payload));
    },
    [dispatch]
  );

  return {
    events,
    isLoading,
    meta,
    filters,
    eventDetails,

    getEventSubscriptionsList,
    setVoucherSpecs,
    setFilters,
    createEvent,
    updateEvent,
    getEventDetails,
  };
};
